import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import SummaryCard from './components/SummaryCard';
import CreditPortfolioTable from './components/CreditPortfolioTable';
import ActivityFeed from './components/ActivityFeed';
import PortfolioChart from './components/PortfolioChart';
import QuickActions from './components/QuickActions';
import FilterControls from './components/FilterControls';
import Button from '../../components/ui/Button';


const Dashboard = () => {
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [filters, setFilters] = useState({});
  const [filteredCredits, setFilteredCredits] = useState([]);

  // Mock data for dashboard
  const summaryData = [
    {
      title: "Total Credits Owned",
      value: "2,847,392",
      subtitle: "tCO2e across 12 registries",
      icon: "Leaf",
      trend: 12.5,
      status: "success"
    },
    {
      title: "Pending Transactions",
      value: "47",
      subtitle: "Awaiting registry confirmation",
      icon: "Clock",
      trend: -8.2,
      status: "warning"
    },
    {
      title: "Average Integrity Score",
      value: "87.3%",
      subtitle: "Portfolio-weighted average",
      icon: "Shield",
      trend: 3.1,
      status: "success"
    },
    {
      title: "Article 6 Compliance",
      value: "94.2%",
      subtitle: "Credits with CA authorization",
      icon: "CheckCircle",
      trend: 1.8,
      status: "success"
    }
  ];

  const mockCredits = [
    {
      id: 1,
      serialNumber: "VCS-2024-001-12345",
      projectName: "Amazon Rainforest Conservation",
      registry: "Verra",
      methodology: "VM0007 - REDD+ Methodology",
      vintage: 2023,
      quantity: 125000,
      ccpStatus: "CCP-Approved",
      integrityScore: 92,
      article6Status: "Authorized"
    },
    {
      id: 2,
      serialNumber: "GS-2024-002-67890",
      projectName: "Solar Farm Development - India",
      registry: "Gold Standard",
      methodology: "GS Energy - Solar",
      vintage: 2024,
      quantity: 89500,
      ccpStatus: "Under Review",
      integrityScore: 88,
      article6Status: "Pending"
    },
    {
      id: 3,
      serialNumber: "ACR-2023-003-11111",
      projectName: "Methane Capture - Landfill",
      registry: "ACR",
      methodology: "ACR Methodology - Landfill Gas",
      vintage: 2023,
      quantity: 67800,
      ccpStatus: "CCP-Approved",
      integrityScore: 85,
      article6Status: "Not Applicable"
    },
    {
      id: 4,
      serialNumber: "ART-2024-004-22222",
      projectName: "Forest Protection - Brazil",
      registry: "ART",
      methodology: "ART TREES - Avoided Deforestation",
      vintage: 2024,
      quantity: 234500,
      ccpStatus: "Non-Compliant",
      integrityScore: 65,
      article6Status: "Rejected"
    },
    {
      id: 5,
      serialNumber: "GCC-2023-005-33333",
      projectName: "Wind Energy - Morocco",
      registry: "GCC",
      methodology: "GCC Wind Energy Methodology",
      vintage: 2023,
      quantity: 156700,
      ccpStatus: "CCP-Approved",
      integrityScore: 91,
      article6Status: "Authorized"
    }
  ];

  const mockActivities = [
    {
      id: 1,
      type: "transfer",
      title: "Credit Transfer Completed",
      description: "125,000 tCO2e transferred from Verra registry",
      timestamp: new Date(Date.now() - 900000),
      hasProvenance: true,
      metadata: {
        serialNumber: "VCS-2024-001-12345",
        quantity: 125000,
        registry: "Verra"
      }
    },
    {
      id: 2,
      type: "integrity_update",
      title: "Integrity Score Updated",
      description: "Solar Farm project score increased to 88%",
      timestamp: new Date(Date.now() - 1800000),
      hasProvenance: true,
      metadata: {
        serialNumber: "GS-2024-002-67890",
        registry: "Gold Standard"
      }
    },
    {
      id: 3,
      type: "claim_created",
      title: "VCMI Claim Generated",
      description: "Offsetting claim created for Q3 2024 emissions",
      timestamp: new Date(Date.now() - 3600000),
      hasProvenance: false,
      metadata: {
        quantity: 45000
      }
    },
    {
      id: 4,
      type: "verification",
      title: "Third-Party Verification",
      description: "Methodology compliance verified for ART project",
      timestamp: new Date(Date.now() - 7200000),
      hasProvenance: true,
      metadata: {
        serialNumber: "ART-2024-004-22222",
        registry: "ART"
      }
    },
    {
      id: 5,
      type: "retirement",
      title: "Credits Retired",
      description: "67,800 tCO2e permanently retired for corporate claims",
      timestamp: new Date(Date.now() - 10800000),
      hasProvenance: true,
      metadata: {
        serialNumber: "ACR-2023-003-11111",
        quantity: 67800,
        registry: "ACR"
      }
    }
  ];

  const portfolioDistributionData = [
    { name: "Forestry & Land Use", value: 1245000, percentage: 43.7 },
    { name: "Renewable Energy", value: 856000, percentage: 30.1 },
    { name: "Energy Efficiency", value: 425000, percentage: 14.9 },
    { name: "Waste Management", value: 198000, percentage: 7.0 },
    { name: "Transportation", value: 89000, percentage: 3.1 },
    { name: "Industrial", value: 34392, percentage: 1.2 }
  ];

  const registryDistributionData = [
    { name: "Verra (VCS)", value: 1156000, percentage: 40.6 },
    { name: "Gold Standard", value: 745000, percentage: 26.2 },
    { name: "ACR", value: 456000, percentage: 16.0 },
    { name: "ART", value: 298000, percentage: 10.5 },
    { name: "GCC", value: 156000, percentage: 5.5 },
    { name: "CAD Trust", value: 36392, percentage: 1.2 }
  ];

  useEffect(() => {
    // Apply filters to credits
    let filtered = mockCredits;
    
    if (filters?.registry) {
      filtered = filtered?.filter(credit => 
        credit?.registry?.toLowerCase()?.includes(filters?.registry?.toLowerCase())
      );
    }
    
    if (filters?.ccpStatus) {
      filtered = filtered?.filter(credit => credit?.ccpStatus === filters?.ccpStatus);
    }
    
    if (filters?.vintage) {
      filtered = filtered?.filter(credit => 
        credit?.vintage?.toString() === filters?.vintage
      );
    }
    
    if (filters?.methodology) {
      filtered = filtered?.filter(credit => 
        credit?.methodology?.toLowerCase()?.includes(filters?.methodology?.toLowerCase())
      );
    }
    
    if (filters?.search) {
      filtered = filtered?.filter(credit => 
        credit?.serialNumber?.toLowerCase()?.includes(filters?.search?.toLowerCase()) ||
        credit?.projectName?.toLowerCase()?.includes(filters?.search?.toLowerCase())
      );
    }
    
    setFilteredCredits(filtered);
  }, [filters]);

  const handleSummaryCardClick = (cardTitle) => {
    switch (cardTitle) {
      case "Total Credits Owned":
        navigate('/credit-portfolio');
        break;
      case "Pending Transactions":
        navigate('/registry-connectors');
        break;
      case "Average Integrity Score":
        navigate('/methodology-intelligence');
        break;
      case "Article 6 Compliance":
        navigate('/article-6-gateway');
        break;
      default:
        break;
    }
  };

  const handleViewDetails = (credit) => {
    navigate('/credit-portfolio', { state: { selectedCredit: credit } });
  };

  const handleCreateClaim = (credit) => {
    navigate('/claims-builder', { state: { selectedCredit: credit } });
  };

  const handleViewProvenance = (activity) => {
    navigate('/provenance-tracking', { state: { activityId: activity?.id } });
  };

  const handleImportCredits = () => {
    navigate('/registry-connectors');
  };

  const handleCreateVCMIClaim = () => {
    navigate('/claims-builder');
  };

  const handleGenerateReport = () => {
    // Mock report generation
    console.log('Generating portfolio report...');
  };

  const handleViewRegistry = () => {
    navigate('/registry-connectors');
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar 
        isCollapsed={sidebarCollapsed} 
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} 
      />
      <main className={`transition-all duration-300 pt-16 ${
        sidebarCollapsed ? 'ml-16' : 'ml-64'
      }`}>
        <div className="p-6 space-y-6">
          {/* Page Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-foreground">Dashboard</h1>
              <p className="text-muted-foreground mt-1">
                Monitor your carbon credit portfolio and platform activities
              </p>
            </div>
            
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                iconName="RefreshCw"
                onClick={() => window.location?.reload()}
              >
                Refresh Data
              </Button>
              <Button
                variant="default"
                iconName="Plus"
                onClick={handleImportCredits}
              >
                Import Credits
              </Button>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {summaryData?.map((card, index) => (
              <SummaryCard
                key={index}
                title={card?.title}
                value={card?.value}
                subtitle={card?.subtitle}
                icon={card?.icon}
                trend={card?.trend}
                status={card?.status}
                onClick={() => handleSummaryCardClick(card?.title)}
              />
            ))}
          </div>

          {/* Charts Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <PortfolioChart
              data={portfolioDistributionData}
              type="pie"
              title="Portfolio by Methodology"
            />
            <PortfolioChart
              data={registryDistributionData}
              type="bar"
              title="Credits by Registry"
            />
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            {/* Credit Portfolio Section */}
            <div className="xl:col-span-2 space-y-6">
              <FilterControls
                onFiltersChange={setFilters}
                totalResults={filteredCredits?.length}
              />
              
              <CreditPortfolioTable
                credits={filteredCredits}
                onViewDetails={handleViewDetails}
                onCreateClaim={handleCreateClaim}
              />
            </div>

            {/* Sidebar Content */}
            <div className="space-y-6">
              <QuickActions
                onImportCredits={handleImportCredits}
                onCreateClaim={handleCreateVCMIClaim}
                onGenerateReport={handleGenerateReport}
                onViewRegistry={handleViewRegistry}
              />
              
              <ActivityFeed
                activities={mockActivities}
                onViewProvenance={handleViewProvenance}
              />
            </div>
          </div>

          {/* System Status Footer */}
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-success rounded-full animate-pulse"></div>
                  <span className="text-sm font-medium text-success">All Systems Operational</span>
                </div>
                <div className="text-sm text-muted-foreground">
                  Last updated: {new Date()?.toLocaleString()}
                </div>
              </div>
              
              <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                <span>Registry Sync: Active</span>
                <span>•</span>
                <span>API Status: Healthy</span>
                <span>•</span>
                <span>Uptime: 99.9%</span>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;